import { useEffect, useState } from "react";
import { API, setSession, getSession, type ClientInfo } from "./api";
import { CGVAcceptance } from "./components/CGVAcceptance";
import { HomeView } from "./components/HomeView";
import { NewSAV } from "./components/NewSAV";
import { SAVDetail } from "./components/SAVDetail";

const TOKEN_CUSTOMER_KEY = "xsavier_widget_last_customer_id";

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "==".slice(0, (4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

type View =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "cgv" }
  | { kind: "home" }
  | { kind: "new" }
  | { kind: "detail"; savId: string };

export default function App({ prestashopToken }: { prestashopToken: string }) {
  const [info, setInfo] = useState<ClientInfo | null>(null);
  const [view, setView] = useState<View>({ kind: "loading" });

  async function loadInfo() {
    const i = await API.clientInfo();
    setInfo(i);
    setView((prev) => {
      if (prev.kind === "loading" || prev.kind === "error" || prev.kind === "cgv") {
        return i.cgv_accepted ? { kind: "home" } : { kind: "cgv" };
      }
      return prev;
    });
  }

  useEffect(() => {
    async function boot() {
      try {
        const payload = decodeJwtPayload(prestashopToken);
        const tokenCustomer = payload?.customer_id ? String(payload.customer_id) : null;
        let sessionCustomer: string | null = null;
        try {
          sessionCustomer = window.sessionStorage.getItem(TOKEN_CUSTOMER_KEY);
        } catch { /* ignore */ }
        const needsAuth = !getSession() || (tokenCustomer && tokenCustomer !== sessionCustomer);
        if (needsAuth) {
          setSession(null);
          const auth = await API.auth(prestashopToken);
          setSession(auth.session_token);
          try {
            if (tokenCustomer)
              window.sessionStorage.setItem(TOKEN_CUSTOMER_KEY, tokenCustomer);
          } catch { /* ignore */ }
        }
        await loadInfo();
      } catch (e) {
        setView({ kind: "error", message: (e as Error).message });
      }
    }
    boot();
  }, [prestashopToken]);

  const showHomeButton = view.kind === "new" || view.kind === "detail";

  return (
    <div className="xs-shell">
      <header className="xs-header">
        <div className="xs-header-avatar">X</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="xs-header-title">Xsavier</div>
          <div className="xs-header-sub">
            {info?.email
              ? `Assistant SAV · ${info.email}${info.company ? " · " + info.company : ""}`
              : "Assistant SAV Fourniresto"}
          </div>
        </div>
        {showHomeButton && (
          <button
            className="xs-header-action"
            onClick={() => setView({ kind: "home" })}
          >
            Mes dossiers
          </button>
        )}
      </header>

      {view.kind === "loading" && (
        <div className="xs-main">
          <div className="xs-muted" style={{ textAlign: "center", padding: 32 }}>
            Connexion à Xsavier…
          </div>
        </div>
      )}

      {view.kind === "error" && (
        <div className="xs-main">
          <div className="xs-alert xs-alert-error">
            <strong>Erreur :</strong> {view.message}
          </div>
        </div>
      )}

      {view.kind === "cgv" && (
        <div className="xs-main">
          <CGVAcceptance onAccepted={() => loadInfo()} />
        </div>
      )}

      {view.kind === "home" && (
        <div className="xs-main">
          <HomeView
            onDeclare={() => setView({ kind: "new" })}
            onOpen={(id) => setView({ kind: "detail", savId: id })}
          />
        </div>
      )}

      {view.kind === "new" && (
        <div className="xs-main">
          <NewSAV onCreated={(s) => setView({ kind: "detail", savId: s.id })} />
        </div>
      )}

      {view.kind === "detail" && (
        <SAVDetail savId={view.savId} onBack={() => setView({ kind: "home" })} />
      )}
    </div>
  );
}
